import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useState, useEffect } from 'react';

export type StatusState = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';

export function useBizQuery<TQueryFnData, TError, TQueryKey extends readonly unknown[]>(
	queryKey: TQueryKey,
	queryFn: () => Promise<TQueryFnData>,
	enabled?: boolean,
	staleTimeSeconds?: number
) {
	const [statusState, setStatusState] = useState<StatusState>('INITIAL');

	const queryOptions: UseQueryOptions<TQueryFnData, TError, TQueryFnData> = {
		queryKey,
		queryFn,
		enabled,
	};

	if (staleTimeSeconds !== undefined) {
		queryOptions.staleTime = staleTimeSeconds * 1000;
	}

	const { data, error, isLoading, isSuccess, isError, refetch } = useQuery<TQueryFnData, TError, TQueryFnData>(queryOptions);

	useEffect(() => {
		if (isLoading) {
			setStatusState('LOADING');
		} else if (isError) {
			setStatusState('ERROR');
		} else if (isSuccess) {
			setStatusState('SUCCESS');
		}
	}, [isLoading, isError, isSuccess]);

	return { data, error, statusState, refetch };
}

export function useBizMutation<TData, TError, TParams, TContext, TQueryKey extends readonly unknown[]>(
	mutationFn: (params: TParams) => Promise<TData>,
	options?: {
		getQueryKeyForInvalidation?: (variables: TParams) => TQueryKey[];
		onSuccessFn?: (result: TData) => void;
	}
) {
	const queryClient = useQueryClient();
	const [statusState, setStatusState] = useState<StatusState>('INITIAL');
	const mutation = useMutation<TData, TError, TParams, TContext>({
		mutationFn,
		onMutate: async () => {
			setStatusState('LOADING');
			return undefined;
		},
		onSuccess: (result, variables) => {
			setStatusState('SUCCESS');
			if (options?.onSuccessFn) {
				options.onSuccessFn(result);
			}
			if (options?.getQueryKeyForInvalidation) {
				const queryKeys = options.getQueryKeyForInvalidation(variables);
				queryKeys.forEach((keys) => {
					queryClient.invalidateQueries({ queryKey: keys });
				});
			}
		},
		onError: () => {
			setStatusState('ERROR');
		},
		onSettled: () => {
			setTimeout(() => setStatusState('INITIAL'), 3000);
		},
	});

	return { ...mutation, statusState };
}
